import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from '@angular/router';
import { HookParserEntry, provideDynamicHooks } from 'ngx-dynamic-hooks';
import { AnalyticsService } from 'analytics';
import {
  calcEnvironmentUrls,
  ENVIRONMENT_URLS_CONFIG_TOKEN,
  EnvironmentUrlsConfig,
  translationDynamicValues,
} from 'app-initialization';
import { AuthInterceptor } from 'auth-data-access';
import { BRAND_CONFIG, BrandConfig, BrandService } from 'brand';
import { EshopProductService } from 'eshop-product';
import { EshopPromotionService } from 'eshop-promotion';
import { FeatureFlagService } from 'feature-flag';
import { LinkComponent } from 'link';
import { CustomErrorHandler, httpErrorInterceptor } from 'monitoring';
import { CmsService, PRISMIC_ENDPOINT } from 'prismic';
import { ProductService } from 'product-shared';
import { PromotionService } from 'promotion-shared';
import { SPRINKLR_APP_ID } from 'sprinklr-webwidget';
import { TooltipComponent } from 'tooltip';
import { TranslateService, TRANSLATION_DYNAMIC_VALUES, TRANSLATION_PRISMIC_TAG } from 'translate';
import { App, BrowserService, FALLBACK_LANGUAGE } from 'utils';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

const initFeatureFlags = (featureFlagService: FeatureFlagService, analyticsService: AnalyticsService) => () =>
  featureFlagService.init(environment.featureFlagClientKey, data => analyticsService.addToDataLayer(data));

const environmentUrlsConfig = (browser: BrowserService): EnvironmentUrlsConfig =>
  calcEnvironmentUrls(environment, browser.getOrigin());

const componentParsers: Array<HookParserEntry> = [
  { component: TooltipComponent },
  {
    component: LinkComponent,
    inputsWhitelist: ['link'],
  },
];

const translationDynamicValuesConfig = (brandService: BrandService) => translationDynamicValues(brandService.brandName);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptorsFromDi(), withFetch(), withInterceptors([httpErrorInterceptor])),
    provideAnimationsAsync(),
    provideDynamicHooks(componentParsers),
    provideAppInitializer(() => {
      const initializerFn = ((cmsService: CmsService, translateService: TranslateService) => async () => {
        await cmsService.init();
        return translateService.initialize();
      })(inject(CmsService), inject(TranslateService));
      return initializerFn();
    }),
    { provide: APP_ID, useValue: App.support },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: TRANSLATION_DYNAMIC_VALUES, useFactory: translationDynamicValuesConfig, deps: [BrandService] },
    { provide: TRANSLATION_PRISMIC_TAG, useValue: App.support },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: ENVIRONMENT_URLS_CONFIG_TOKEN, useFactory: environmentUrlsConfig, deps: [BrowserService] },
    { provide: BRAND_CONFIG, useValue: <BrandConfig>{ brand: environment.brand } },
    { provide: FALLBACK_LANGUAGE, useValue: environment.fallbackLang },
    { provide: PRISMIC_ENDPOINT, useValue: environment.prismicEndpoint },
    { provide: ProductService, useClass: EshopProductService },
    { provide: PromotionService, useClass: EshopPromotionService },
    { provide: SPRINKLR_APP_ID, useValue: environment.sprinklerAppId },
    { provide: LOCALE_ID, useValue: 'de-CH' },
    provideAppInitializer(() => {
      const initializerFn = initFeatureFlags(inject(FeatureFlagService), inject(AnalyticsService));
      return initializerFn();
    }),
  ],
};
